import { cn } from '@/lib/utils';

/**
 * PanelHeader — plain div, no Sheet dependency.
 * Figma: calendar@grid@week-view@create-appt
 *   [Cancel (blue)] | [Title centered] | [Create (active when form is valid)]
 *
 * The right-slot button is a duplicate save trigger — Flutter's Cancel/
 * Title/Create header (`create_appointment_widget.dart` AppBar) wires
 * the same action as the footer's Book Appointment button so the user
 * can submit from either end of the panel.
 */

interface PanelHeaderProps {
  mode: 'create' | 'edit' | 'reschedule' | 'cancel';
  isValid: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSave: () => void;
  /**
   * Copy overrides for a rail that is not booking an appointment with a
   * practitioner (PROD-2186: the device rail books a DEVICE, and reading
   * "Create Appointment" over a device picker is wrong). Omitted everywhere
   * else, so the practitioner rails keep the Flutter-parity copy verbatim.
   */
  title?: string;
  saveLabel?: string;
  savingLabel?: string;
}

const TITLES = {
  create: 'Create Appointment',
  edit: 'Edit Appointment',
  reschedule: 'Reschedule Appointment',
  cancel: 'Cancel Appointment',
};

export function PanelHeader({
  mode,
  isValid,
  isSubmitting,
  onClose,
  onSave,
  title,
  saveLabel,
  savingLabel,
}: PanelHeaderProps) {
  const label =
    mode === 'cancel'
      ? '' // hide the right action when cancelling
      : isSubmitting
        ? (savingLabel ?? 'Saving…')
        : (saveLabel ?? (mode === 'create' ? 'Create' : 'Save'));
  const enabled = isValid && !isSubmitting;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-border-default flex-shrink-0">
      <button
        onClick={onClose}
        className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
      >
        Cancel
      </button>
      <h2 className="text-base font-bold text-text-primary">
        {title ?? TITLES[mode]}
      </h2>
      {label ? (
        <button
          type="button"
          onClick={onSave}
          disabled={!enabled}
          className={cn(
            'text-sm font-medium w-[64px] text-right',
            enabled
              ? 'text-brand-primary hover:text-interactive-hover'
              : 'text-text-hint cursor-not-allowed',
          )}
        >
          {label}
        </button>
      ) : (
        <span className="w-[64px]" />
      )}
    </div>
  );
}
